import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Row, Col, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import TopBar from '../../../components/TopBar';
import { 
  getEmployees,
  getEmployeeById
} from '../../../services/employeeService';
import { getAppraisal } from '../services/appraisalService';
import { 
  createAppraisal,
  updateAppraisal
} from '../../../services/appraisalService';
import api from '../../../services/api';

const AppraisalForm = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const { 
    employee: propEmployee,
    onSuccess,
    onCancel,
    refetchAppraisals 
  } = props;
  
  const [designations, setDesignations] = useState([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: propEmployee?.id || '',
    reviewer_id: '19',
    reviewPeriodStart: moment().format('YYYY-MM-DD'),
    reviewPeriodEnd: moment().add(1, 'year').format('YYYY-MM-DD'),
    goals: [{ description: '', targetDate: '', status: 'Not Started' }],
    skills: [{ name: '', rating: 3, notes: '' }],
    overallRating: 3,
    strengths: '',
    areasForImprovement: '',
    comments: '',
    status: 'in_progress',
    currentSalary: propEmployee?.base_salary || '',
    incrementPercentage: '',
    updatedSalary: '',
    nextAppraisalDate: '',
    employeeType: propEmployee?.employee_type || '',
    designationId: propEmployee?.designation_id || '',
    enablePF: propEmployee?.pf_enabled || false,
    salaryComponents: {
      basicPay: { percentage: 40, amount: 0 },
      da: { percentage: 30, amount: 0 },
      medical: { percentage: 8, amount: 0 },
      hra: { percentage: 20, amount: 0 },
      travel: { amount: 0 }
    },
    deductions: {
      pf: { employee: 0, employer: 0 },
      esi: { employee: 0, employer: 0 }
    },
    grossSalary: 0,
    netSalary: 0
  });
  
  // Calculate salary components whenever currentSalary or incrementPercentage changes
  useEffect(() => {
    if (formData.currentSalary && formData.incrementPercentage) {
      const currentSalary = parseFloat(formData.currentSalary) || 0;
      const increment = parseFloat(formData.incrementPercentage) || 0;
      const updatedSalary = currentSalary + (currentSalary * increment) / 100;
      
      // Calculate salary components
      const basicPay = updatedSalary * 0.4; // 40%
      const da = updatedSalary * 0.3;       // 30%
      const medical = updatedSalary * 0.08;  // 8%
      const hra = updatedSalary * 0.2;       // 20%
      const travel = Math.min(1600, updatedSalary * 0.02); // Max 1600 or 2%
      
      // Calculate deductions
      const pfEligible = formData.enablePF;
      const pfEmployee = pfEligible ? Math.min(updatedSalary * 0.12, 1800) : 0; // 12% of basic or 1800 max
      const pfEmployer = pfEligible ? Math.min(updatedSalary * 0.12, 1800) : 0; // 12% of basic or 1800 max
      
      // ESI (if gross salary < 21000)
      const grossSalary = basicPay + da + medical + hra + travel;
      const esiEmployee = grossSalary < 21000 ? grossSalary * 0.0075 : 0; // 0.75%
      const esiEmployer = grossSalary < 21000 ? grossSalary * 0.0325 : 0; // 3.25%
      
      // Calculate net salary
      const totalDeductions = pfEmployee + esiEmployee;
      const netSalary = grossSalary - totalDeductions;
      
      setFormData(prev => ({
        ...prev,
        updatedSalary: updatedSalary.toFixed(2),
        salaryComponents: {
          basicPay: { ...prev.salaryComponents.basicPay, amount: basicPay },
          da: { ...prev.salaryComponents.da, amount: da },
          medical: { ...prev.salaryComponents.medical, amount: medical },
          hra: { ...prev.salaryComponents.hra, amount: hra },
          travel: { ...prev.salaryComponents.travel, amount: travel }
        },
        deductions: {
          pf: { employee: pfEmployee, employer: pfEmployer },
          esi: { employee: esiEmployee, employer: esiEmployer }
        },
        grossSalary,
        netSalary
      }));
    }
  }, [formData.currentSalary, formData.incrementPercentage, formData.enablePF]);

  // Update form data when employee prop changes
  useEffect(() => {
    if (propEmployee) {
      setFormData(prev => ({
        ...prev,
        employeeId: propEmployee.id,
        currentSalary: propEmployee.base_salary || '',
        employeeType: propEmployee.employee_type || '',
        designationId: propEmployee.designation_id || '',
        enablePF: propEmployee.pf_enabled || false
      }));
    }
  }, [propEmployee]);
  
  // Calculate updated salary when current salary or increment percentage changes
  useEffect(() => {
    if (formData.currentSalary && formData.incrementPercentage) {
      const current = parseFloat(formData.currentSalary) || 0;
      const increment = (parseFloat(formData.incrementPercentage) || 0) / 100;
      const updated = current + (current * increment);
      setFormData(prev => ({
        ...prev,
        updatedSalary: updated.toFixed(2)
      }));
    }
  }, [formData.currentSalary, formData.incrementPercentage]);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Set default next appraisal date when status changes to in_progress
  // useEffect(() => {
  //   if (formData.status === 'in_progress' && !formData.nextAppraisalDate) {
  //     // Set default next appraisal date to 1 year from now
  //     const nextYear = moment().add(1, 'year').format('YYYY-MM-DD');
  //     setFormData(prev => ({
  //       ...prev,
  //       nextAppraisalDate: nextYear
  //     }));
  //   }
  // }, [formData.status, formData.nextAppraisalDate]);

  // Fetch designations
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        setLoadingDesignations(true);
        const response = await api.get('/designations');
        const data = await response.data;
        console.log('Designations API Response:', data); // Debug log
        setDesignations(data|| []);
      } catch (err) {
        console.error('Error fetching designations:', err);
        setError('Failed to load designations');
      } finally {
        setLoadingDesignations(false);
      }
    };
    
    fetchDesignations();
  }, []);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    // Fetch employees for the dropdown
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const response = await getEmployees();
        
        // The API returns the data directly, not wrapped in a data property
        const employeesData = Array.isArray(response.data) ? response.data : [response.data];
        
        const employeeOptions = employeesData.map(emp => ({
          id: emp.id,
          name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
          position: emp.designation?.name || 'Not specified',
          department: emp.department?.name || 'Not specified',
          base_salary: emp.base_salary || 0,
          pf_enabled: emp.pf_enabled || false
        }));
        
        setEmployees(employeeOptions);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError(err.response?.data?.message || 'Failed to load employees');
      } finally {
        setLoading(false);
      }
    };

    // If editing, load the appraisal data
    const fetchAppraisal = async () => {
      if (!isEditing || !id) return;
      
      try {
        setLoading(true);
        const data = await getAppraisal(id);
        
        if (data) {
          // Default values for salary components and deductions
          const defaultSalaryComponents = {
            basicPay: { percentage: 40, amount: data.basic_pay || 0 },
            da: { percentage: 30, amount: data.da || 0 },
            medical: { percentage: 8, amount: data.medical || 0 },
            hra: { percentage: 20, amount: data.hra || 0 },
            travel: { amount: data.travel_allowance || 0 }
          };

          const defaultDeductions = {
            pf: { 
              employee: data.pf_employee || 0, 
              employer: data.pf_employer || 0 
            },
            esi: { 
              employee: data.esi_employee || 0, 
              employer: data.esi_employer || 0 
            }
          };
          
          // Format the data for the form
          const formattedData = {
            employeeId: data.employee_id,
            reviewer_id: data.reviewer_id,
            currentSalary: data.current_salary || '',
            incrementPercentage: data.increment_percentage || '',
            updatedSalary: data.updated_salary || '',
            reviewPeriodStart: data.review_period_start ? moment(data.review_period_start).format('YYYY-MM-DD') : '',
            reviewPeriodEnd: data.review_period_end ? moment(data.review_period_end).format('YYYY-MM-DD') : '',
            nextAppraisalDate: data.next_appraisal_date ? moment(data.next_appraisal_date).format('YYYY-MM-DD') : '',
            goals: Array.isArray(data.goals) ? data.goals.map(goal => ({
              id: goal.id,
              description: goal.description || '',
              targetDate: goal.target_date ? moment(goal.target_date).format('YYYY-MM-DD') : '',
              status: goal.status || 'not_started',
              notes: goal.notes || '',
              weight: goal.weight || 0
            })) : [],
            skills: Array.isArray(data.skills) ? data.skills.map(skill => ({
              id: skill.id,
              name: skill.skill_name || '',
              rating: skill.rating || 0,
              notes: skill.notes || '',
              category: skill.category || ''
            })) : [],
            overallRating: data.overall_rating || 0,
            strengths: data.strengths || '',
            areasForImprovement: data.areas_for_improvement || '',
            comments: data.comments || '',
            status: data.status || 'in_progress',
            enablePF: data.enable_pf || false,
            salaryComponents: {
              ...defaultSalaryComponents,
              ...(data.salary_components || {})
            },
            deductions: {
              ...defaultDeductions,
              ...(data.deductions || {})
            },
            grossSalary: data.gross_salary || 0,
            netSalary: data.net_salary || 0
          };
          
          setFormData(formattedData);
        }
      } catch (err) {
        console.error('Error loading appraisal:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            headers: err.config?.headers
          }
        });
        
        const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to load appraisal. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
    fetchAppraisal();
  }, [id, isEditing]);

  // Handle employee change (only applicable when employee is not pre-selected)
  const handleEmployeeChange = async (e) => {
    if (propEmployee) return; // Skip if employee is pre-selected
    
    const employeeId = e.target.value;

    // Update the employee ID immediately
    setFormData(prev => ({
      ...prev,
      employeeId,
      currentSalary: '' // Clear current salary to trigger recalculation
    }));

    // If we have an employee ID, find the selected employee in the employees list
    if (employeeId && employees.length > 0) {
      const selectedEmployee = employees.find(emp => emp.id.toString() === employeeId);
      if (selectedEmployee) {
        setFormData(prev => ({
          ...prev,
          currentSalary: selectedEmployee.base_salary || '',
          // Reset other salary fields when changing employee
          incrementPercentage: '',
          updatedSalary: ''
        }));
      } else {
        // If employee not found in the list, try to fetch their details
        try {
          const response = await getEmployeeById(employeeId);
          if (response.data) {
            setFormData(prev => ({
              ...prev,
              currentSalary: response.data.base_salary || '',
              incrementPercentage: '',
              updatedSalary: ''
            }));
          }
        } catch (error) {
          console.error('Error fetching employee details:', error);
          setError('Failed to load employee details. Please try again.');
        }
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'enablePF') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    
    // If current salary or increment percentage changes, we'll let the useEffect handle the calculation
    if (name === 'incrementPercentage' || name === 'currentSalary') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        // Reset updated salary if either field is empty
        ...(name === 'currentSalary' && !formData.incrementPercentage ? { updatedSalary: '' } : {}),
        ...(name === 'incrementPercentage' && !formData.currentSalary ? { updatedSalary: '' } : {})
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleGoalChange = (index, e) => {
    const { name, value } = e.target;
    const updatedGoals = [...formData.goals];
    updatedGoals[index] = { 
      ...updatedGoals[index], 
      [name]: name === 'targetDate' ? moment(value).format('YYYY-MM-DD') : value 
    };
    setFormData(prev => ({ ...prev, goals: updatedGoals }));
  };

  const handleSkillChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSkills = [...formData.skills];
    updatedSkills[index] = { ...updatedSkills[index], [name]: value };
    setFormData(prev => ({ ...prev, skills: updatedSkills }));
  };

  const addGoal = () => {
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, { description: '', targetDate: '', status: 'Not Started' }]
    }));
  };

  const removeGoal = (index) => {
    const updatedGoals = formData.goals.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, goals: updatedGoals }));
  };

  // Define the fixed skill set
  const SKILL_SET = [
    'Capability in task verticals',
    'Vertical Proficiency',
    'Average Efficiency expectation',
    'Work Dependability- Primary Vertical',
    'Toolset and technicalities knowhow',
    'Communication',
    'Work Dependability-Secondary Vertical',
    'Artistic/Conceptual Problem solving'
  ];

  useEffect(() => {
    // Ensure skills array always matches the SKILL_SET
    setFormData(prev => ({
      ...prev,
      skills: SKILL_SET.map((name, i) => ({
        name,
        rating: prev.skills && prev.skills[i] ? prev.skills[i].rating : 3,
        notes: prev.skills && prev.skills[i] ? prev.skills[i].notes : ''
      }))
    }));
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.employeeId) {
      setError('Please select an employee');
      return;
    }
    
    // Additional validation when completing the appraisal
    if (formData.status === 'completed') {
      // Check if updatedSalary is a valid number
      if (formData.updatedSalary === '' || isNaN(parseFloat(formData.updatedSalary))) {
        setError('Please enter a valid updated salary');
        return;
      }
      
      // Ensure updatedSalary is a positive number
      const updatedSalary = parseFloat(formData.updatedSalary);
      if (updatedSalary <= 0) {
        setError('Updated salary must be greater than 0');
        return;
      }
      
      if (!formData.employeeType) {
        setError('Employee type is required when completing the appraisal');
        return;
      }
      if (!formData.designationId) {
        setError('Designation is required when completing the appraisal');
        return;
      }
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Format the data for the API
      const formattedData = {
        employee_id: formData.employeeId,
        reviewer_id: formData.reviewer_id,
        review_period_start: formData.reviewPeriodStart,
        review_period_end: formData.reviewPeriodEnd,
        goals: formData.goals.map(goal => ({
          description: goal.description,
          target_date: goal.targetDate,
          status: goal.status,
          weight: parseFloat(goal.weight) || 0
        })),
        skills: formData.skills.map(skill => ({
          skill_name: skill.name,
          rating: parseFloat(skill.rating) || 0,
          notes: skill.notes,
          category: skill.category
        })),
        strengths: formData.strengths,
        areas_for_improvement: formData.areasForImprovement,
        comments: formData.comments,
        status: formData.status.toLowerCase(),
        overall_rating: parseFloat(formData.overallRating),
        current_salary: formData.currentSalary ? parseFloat(formData.currentSalary) : null,
        increment_percentage: formData.incrementPercentage ? parseFloat(formData.incrementPercentage) : null,
        updated_salary: formData.updatedSalary ? parseFloat(formData.updatedSalary) : null,
        employee_type: formData.employeeType,
        designation_id: formData.designationId,
        next_appraisal_date: formData.nextAppraisalDate
      };

      
      let result;
      if (isEditing) {
        result = await updateAppraisal(id, formattedData);
      } else {
        result = await createAppraisal(formattedData);
      }
      
      // Call the onSuccess callback
      if (onSuccess) {
        onSuccess(result);
      } else {
        // Fallback navigation if no onSuccess callback
        navigate('/app/appraisals');
      }
    } catch (err) {
      console.error('Error saving appraisal:', err);
      setError(err.response?.data?.message || 'Failed to save appraisal. Please check the form and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  if (saving) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  return (
    <>
    <TopBar />
    <Container>
    <div className="p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">{isEditing ? 'Edit Appraisal' : 'New Appraisal'}</h3>
        {/* <Button 
          variant="outline-secondary" 
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button> */}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Review</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group controlId="employeeId" className="mb-3">
                  <Form.Label>Employee</Form.Label>
                  {propEmployee ? (
                    <Form.Control 
                      type="text" 
                      value={`${propEmployee.name} `} 
                      readOnly 
                    />
                  ) : (
                    <>
                      <Form.Select 
                        value={formData.employeeId} 
                        onChange={handleEmployeeChange}
                        name="employeeId"
                        required
                        disabled={isEditing || loading}
                      >
                        <option value="">Select an employee</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id} data-base-salary={emp.base_salary}>
                            {emp.name} - {emp.department}
                          </option>
                        ))}
                      </Form.Select>
                      {loading && <div className="mt-2"><Spinner animation="border" size="sm" /> Loading employees...</div>}
                    </>
                  )}
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Review Period Start</Form.Label>
                  <Form.Control
                    type="date"
                    name="reviewPeriodStart"
                    value={formData.reviewPeriodStart}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Review Period End</Form.Label>
                  <Form.Control
                    type="date"
                    name="reviewPeriodEnd"
                    value={formData.reviewPeriodEnd}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              {/* <Col md={6}>
                <Form.Group controlId="reviewer_id" className="mb-3">
                  <Form.Label>Reviewer</Form.Label>
                  <Form.Select 
                    value={formData.reviewer_id || ''} 
                    onChange={handleChange}
                    name="reviewer_id"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a reviewer</option>
                    {employees
                      .filter(emp => emp.id.toString() !== formData.employeeId) // Don't allow selecting self as reviewer
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} - {emp.department}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col> */}
              {/* <Col md={6}>
                <Form.Group controlId="reviewerId" className="mb-3">
                  <Form.Label>Reviewer</Form.Label>
                  <Form.Select 
                    value={formData.reviewer_id || ''} 
                    onChange={handleChange}
                    name="reviewer_id"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a reviewer</option>
                    {employees
                      .filter(emp => emp.id.toString() !== formData.employeeId)
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} - {emp.department}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col> */}
            </Row>
          </Card.Body>
        </Card>

        {/* <Card className="mb-4">
          <Card.Header>Review Period</Card.Header>
          <Card.Body>
            <Row>
             
            </Row>
          </Card.Body>
        </Card> */}

        <Card className="mb-4">
          <Card.Header>Goals & Objectives</Card.Header>
          <Card.Body>
            {formData.goals.map((goal, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Goal Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="description"
                        value={goal.description}
                        onChange={(e) => handleGoalChange(index, e)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Target Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="targetDate"
                        value={goal.targetDate}
                        onChange={(e) => handleGoalChange(index, e)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={goal.status}
                        onChange={(e) => handleGoalChange(index, e)}
                        required
                      >
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on_hold">On Hold</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {/* <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="outline-danger"
                      onClick={() => removeGoal(index)}
                      disabled={formData.goals.length <= 1}
                    >
                      ×
                    </Button>
                  </Col> */}
                </Row>
              </div>
            ))}
            {/* <Button variant="outline-primary" onClick={addGoal} className="mt-2">
              + Add Goal
            </Button> */}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Skills Assessment</Card.Header>
          <Card.Body>
            {formData.skills.map((skill, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Skill Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={skill.name}
                        readOnly
                        plaintext
                        style={{ fontWeight: 'bold' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rating (1-5)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="5"
                        step="0.5"
                        name="rating"
                        value={skill.rating}
                        onChange={e => handleSkillChange(index, e)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        name="notes"
                        value={skill.notes}
                        onChange={e => handleSkillChange(index, e)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Overall Assessment</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Overall Rating (1-5)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    name="overallRating"
                    value={formData.overallRating}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            
              {/* Employee Type and Designation fields */}
              {(formData.status === 'completed' || formData.status === 'in_progress') && (
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Employee Type</Form.Label>
                    <Form.Select 
                      name="employeeType" 
                      value={formData.employeeType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Employee Type</option>
                      <option value="Intern">Intern</option>
                      <option value="Trainee">Trainee</option>
                      <option value="Probation">Probation</option>
                      <option value="Contract">Contract</option>
                      <option value="Permanent">Permanent</option>
                      <option value="Freelancer">Freelancer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              {(formData.status === 'completed' || formData.status === 'in_progress') && (
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Designation</Form.Label>
                    {loadingDesignations ? (
                      <Form.Control type="text" placeholder="Loading designations..." disabled />
                    ) : (
                      <Form.Select
                        name="designationId"
                        value={formData.designationId}
                        onChange={handleChange}
                        required={formData.status === 'completed' || formData.status === 'in_progress'}
                      >
                        <option value="">Select Designation</option>
                        {designations.map(designation => (
                          <option key={designation.id} value={designation.id}>
                            {designation.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              )}


              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.status === 'in_progress' && (
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Appraisal Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="nextAppraisalDate"
                      value={formData.nextAppraisalDate || ''}
                      onChange={handleChange}
                      min={moment().add(1, 'day').format('YYYY-MM-DD')}
                      required
                    />
                    <Form.Text className="text-muted">
                      Please select the date for the next performance review.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
          
            </Row>


<Row>
            <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Salary</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="currentSalary"
                      value={formData.currentSalary || ''}
                      readOnly
                      placeholder="Current Salary"
                      min="0"
                      step="0.01"
                      className="bg-light"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Increment Percentage</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      name="incrementPercentage"
                      value={formData.incrementPercentage}
                      onChange={handleChange}
                      placeholder="Increment %"
                      min="0"
                      max="100"
                      step="0.01"
                      required={formData.status === 'Completed'}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Updated Salary</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="updatedSalary"
                      value={formData.updatedSalary ? `₹${parseFloat(formData.updatedSalary).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                      readOnly
                      className="bg-light"
                      placeholder="Will be calculated"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              
            </Row>
            
            {/* Salary Breakdown */}
            {!isEditing && (
            <Card className="mb-4">
              <Card.Header>Salary Breakdown</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <h6>Components</h6>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th className="text-end">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Basic Pay (40%)</td>
                          <td className="text-end">{formData.salaryComponents.basicPay.amount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>DA (30%)</td>
                          <td className="text-end">{formData.salaryComponents.da.amount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>Medical (8%)</td>
                          <td className="text-end">{formData.salaryComponents.medical.amount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>HRA (20%)</td>
                          <td className="text-end">{formData.salaryComponents.hra.amount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>Travel Allowance (max 1600)</td>
                          <td className="text-end">{formData.salaryComponents.travel.amount.toFixed(2)}</td>
                        </tr>
                        <tr className="table-active">
                          <td><strong>Gross Salary</strong></td>
                          <td className="text-end"><strong>{formData.grossSalary.toFixed(2)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                  <Col md={6}>
                    <h6>Deductions</h6>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Deduction</th>
                          <th className="text-end">Employee</th>
                          <th className="text-end">Employer</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                            <Form.Check 
                              type="switch"
                              id="enable-pf"
                              label={formData.enablePF ? 'PF Enabled' : 'PF Disabled'}
                              name="enablePF"
                              checked={formData.enablePF}
                              onChange={handleChange}
                              disabled={true}
                              className={!propEmployee?.pf_enabled ? 'opacity-50' : ''}
                            />  
                              {!propEmployee?.pf_enabled && (
                                <small className="ms-2 text-muted">
                                  (PF is disabled for this employee in their profile)
                                </small>
                              )}
                            </div>
                          </td>
                          <td className="text-end">{formData.deductions.pf.employee.toFixed(2)}</td>
                          <td className="text-end">{formData.deductions.pf.employer.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>ESI {formData.grossSalary < 21000 ? '(Applicable)' : '(Not Applicable)'}</td>
                          <td className="text-end">{formData.deductions.esi.employee.toFixed(2)}</td>
                          <td className="text-end">{formData.deductions.esi.employer.toFixed(2)}</td>
                        </tr>
                        <tr className="table-active">
                          <td><strong>Total Deductions</strong></td>
                          <td className="text-end"><strong>{(formData.deductions.pf.employee + formData.deductions.esi.employee).toFixed(2)}</strong></td>
                          <td className="text-end"><strong>{(formData.deductions.pf.employer + formData.deductions.esi.employer).toFixed(2)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="text-end">
                      <h5>Net Salary: ₹{formData.netSalary.toFixed(2)}</h5>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            )}
            
            <Row>
            </Row>


            
            <Form.Group className="mb-3">
              <Form.Label>Strengths</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="strengths"
                value={formData.strengths}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Areas for Improvement</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="areasForImprovement"
                value={formData.areasForImprovement}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Additional Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="comments"
                value={formData.comments}
                onChange={handleChange}
              />
            </Form.Group>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end gap-2">
          <Button variant="outline-secondary" onClick={() => navigate('/app/employee-appraisals')}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" className="me-1" />
                Saving...
              </>
            ) : isEditing ? (
              'Update Appraisal'
            ) : (
              'Save Appraisal'
            )}
          </Button>
        </div>
      </Form>
    </div>
    </Container>
    </> 
  );
};

export default AppraisalForm;
